import { useCallback, useEffect, useRef, useState } from 'react';
import type { Dispatch, SetStateAction } from 'react';
import { queryClient } from '@/lib/query-client';
import { analyticsV2Service } from '@/services/analyticsV2.service';
import { SCALAR_METRIC_KEYS } from '@/types/analyticsV2';
import type { ScalarMetricsBatch } from '@/types/analyticsV2';

const METRIC_STALE_TIME = 60 * 1000;

// Cap on in-flight requests (one per account) — many small parallel scans, never one query spanning every account.
const MAX_CONCURRENCY = 10;

const KEYS_CACHE_ID = [...SCALAR_METRIC_KEYS].sort().join(',');

export type RowStatus = 'loading' | 'success' | 'error';

export interface AccountRowState {
  status: RowStatus;
  data?: ScalarMetricsBatch;
}

type RowSetter = Dispatch<SetStateAction<Record<number, AccountRowState>>>;

// `cancelled` flips on param change or unmount so a superseded run can't write stale state.
interface Run {
  cancelled: boolean;
}

const batchKey = (accountId: number, fromDate: string, toDate: string) =>
  ['analytics-v2', 'metrics-batch', accountId, fromDate, toDate, KEYS_CACHE_ID] as const;

/**
 * Same key `useSingleMetric` builds, so seeding here is a cache hit on the account page.
 *
 * The trailing `'all'` is the BRAND segment, and it has to be here or the shapes diverge
 * and every seeded entry is written to a key nothing ever reads — the account page would go back
 * to six fresh requests with nothing failing to say why. `'all'` and not a real brand id
 * because this list is account-wide: it spans accounts, so no single brand applies, and a
 * an account page opened from it therefore starts unscoped too.
 */
const perKeyKey = (accountId: number, fromDate: string, toDate: string, key: string) =>
  ['analytics-v2', 'metric', accountId, fromDate, toDate, key, 'all'] as const;

// Warm the per-key cache so the account page's OverviewSection opens from cache, not six fresh requests.
function seedPerKeyCache(accountId: number, fromDate: string, toDate: string, batch: ScalarMetricsBatch) {
  for (const key of SCALAR_METRIC_KEYS) {
    const value = batch[key];
    if (value === undefined) continue;
    queryClient.setQueryData(perKeyKey(accountId, fromDate, toDate, key), { [key]: value });
  }
}

function fetchAccountMetrics(
  accountId: number,
  fromDate: string,
  toDate: string,
  run: Run,
  setRows: RowSetter,
): Promise<void> {
  return queryClient
    .fetchQuery({
      queryKey: batchKey(accountId, fromDate, toDate),
      queryFn: () => analyticsV2Service.getMetrics(SCALAR_METRIC_KEYS, { fromDate, toDate, accountId }),
      staleTime: METRIC_STALE_TIME,
    })
    .then((data) => {
      if (run.cancelled) return;
      seedPerKeyCache(accountId, fromDate, toDate, data);
      setRows((prev) => ({ ...prev, [accountId]: { status: 'success', data } }));
    })
    .catch(() => {
      if (run.cancelled) return;
      setRows((prev) => ({ ...prev, [accountId]: { status: 'error' } }));
    });
}

// At most `max` tasks run at once.
function createLimiter(max: number) {
  let active = 0;
  const queue: Array<() => void> = [];
  const pump = () => {
    while (active < max && queue.length > 0) {
      active++;
      const job = queue.shift()!;
      job();
    }
  };
  return (task: () => Promise<unknown>) => {
    queue.push(() => {
      void task().finally(() => {
        active--;
        pump();
      });
    });
    pump();
  };
}

/**
 * One batched request per account, capped at MAX_CONCURRENCY, each row resolving
 * independently. Cached accounts render instantly; every result seeds the per-key
 * cache so the account page opens warm.
 */
export function useConsolidatedMetrics(
  accountIds: number[],
  fromDate: string,
  toDate: string,
): { rows: Record<number, AccountRowState>; retry: (accountId: number) => void } {
  const [rows, setRows] = useState<Record<number, AccountRowState>>({});
  // Kept in refs so the stable `retry` callback can read the latest values.
  const paramsRef = useRef({ fromDate, toDate });
  paramsRef.current = { fromDate, toDate };
  const runRef = useRef<Run>({ cancelled: false });

  const accountIdsKey = accountIds.join(',');

  useEffect(() => {
    if (!fromDate || !toDate || accountIds.length === 0) {
      setRows({});
      return;
    }
    const run: Run = { cancelled: false };
    runRef.current = run;
    const schedule = createLimiter(MAX_CONCURRENCY);

    // Cache hits render immediately (no skeleton flash); misses queue a throttled fetch.
    const nextRows: Record<number, AccountRowState> = {};
    const toFetch: number[] = [];
    for (const id of accountIds) {
      const cached = queryClient.getQueryData<ScalarMetricsBatch>(batchKey(id, fromDate, toDate));
      if (cached) {
        nextRows[id] = { status: 'success', data: cached };
        seedPerKeyCache(id, fromDate, toDate, cached);
      } else {
        nextRows[id] = { status: 'loading' };
        toFetch.push(id);
      }
    }
    setRows(nextRows);

    for (const id of toFetch) {
      schedule(() => fetchAccountMetrics(id, fromDate, toDate, run, setRows));
    }

    return () => {
      run.cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- accountIds tracked via accountIdsKey
  }, [accountIdsKey, fromDate, toDate]);

  const retry = useCallback((accountId: number) => {
    const { fromDate: from, toDate: to } = paramsRef.current;
    if (!from || !to) return;
    queryClient.removeQueries({ queryKey: batchKey(accountId, from, to) });
    setRows((prev) => ({ ...prev, [accountId]: { status: 'loading' } }));
    void fetchAccountMetrics(accountId, from, to, runRef.current, setRows);
  }, []);

  return { rows, retry };
}
